import { Button, ButtonDropdown, ButtonsGroup, IconButton, NavItem, useModal } from '@billy/design-system'
import { ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useUserOrganization } from '@modules-deprecated/app/organization'

import { ModalId } from '../../../../../../enums/modalId'
import { useVatDeclaration } from '../../../../context/vatDeclarationContext'
import { VatDeclarationStatus } from '../../../../enums/vatDeclarationStatus'
import { getSubmitButtonVariant } from '../../../../utils/getSubmitButtonVariant'
import { ActionModals } from './elements/ActionModals'
import { SideActionsPrimaryButton } from './elements/SideActionsPrimaryButton'
import { SideActionsPrimaryButtonDK } from './elements/SideActionsPrimaryButton/SideActionsPrimaryButtonDK'
import { SideActionsPrimaryButtonNL } from './elements/SideActionsPrimaryButton/SideActionsPrimaryButtonNL'
import { VatDeclarationsDropdownActionType } from './enums/vatDeclarationsDropdownActionType'
import { getVatDeclarationsItems } from './utils/getVatDeclarationsItems'

const vatDeclarationsExportModalId = ModalId.VatDeclarationsExportModal
const vatDeclarationsVoidPeriodPaymentModalId = ModalId.VatDeclarationsVoidPeriodPaymentModal
const vatDeclarationsVoidPeriodSettlementModalId = ModalId.VatDeclarationsVoidPeriodSettlementModal
const vatDeclarationsChangePeriodModalId = ModalId.VatDeclarationsChangePeriodModal
const vatDeclarationsDefinePayoutModalId = ModalId.VatDeclarationsDefinePayoutModal
const vatDeclarationsSettlePeriodModalId = ModalId.VatDeclarationsSettlePeriodModal

interface VatDeclarationSideActionsProps {
  isSkatConnected?: boolean
  onShowIntegrationForm?: (showForm: boolean) => void
}

export const VatDeclarationSideActions = ({
  isSkatConnected,
  onShowIntegrationForm,
}: VatDeclarationSideActionsProps): ReactElement | null => {
  const { organization } = useUserOrganization()
  const { t } = useTranslation()
  const { vatDeclaration, isAdvancedSettlement, isPeriodPaid, isPeriodPayable, isPeriodSettled, submission, status } =
    useVatDeclaration()

  const { open: openExportModal } = useModal(vatDeclarationsExportModalId)
  const { open: openVoidPaymentModal } = useModal(vatDeclarationsVoidPeriodPaymentModalId)
  const { open: openVoidSettlementModal } = useModal(vatDeclarationsVoidPeriodSettlementModalId)
  const { open: openChangePeriodModal } = useModal(vatDeclarationsChangePeriodModalId)
  const { open: openDefinePayoutModal } = useModal(vatDeclarationsDefinePayoutModalId)
  const { open: openSettlementModal } = useModal(vatDeclarationsSettlePeriodModalId)

  const shouldShowDefinePayoutButton =
    isAdvancedSettlement &&
    (status === VatDeclarationStatus.ReadyToSubmit || status === VatDeclarationStatus.OverdueSubmission)
  const shouldShowPrimaryButton =
    !isPeriodPaid && isPeriodPayable === isPeriodSettled && status !== VatDeclarationStatus.Draft

  const submitButtonVariant = getSubmitButtonVariant({
    countryId: organization?.countryId,
    correctionNo: vatDeclaration?.correctionNo,
    isSkatConnected,
    submission,
    status,
  })

  const vatDeclarationsMenuItems: NavItem<VatDeclarationsDropdownActionType>[] = useMemo(
    () =>
      getVatDeclarationsItems({
        country: organization?.countryId,
        isPeriodPaid,
        isPeriodSettled,
        submitButtonVariant,
        t,
      }),
    [isPeriodPaid, isPeriodSettled, submitButtonVariant, organization?.countryId, t],
  )

  const shouldShowMoreDropdown = !!vatDeclarationsMenuItems.length

  const handleDefinePayoutButtonClick = useCallback(() => {
    openDefinePayoutModal()
  }, [openDefinePayoutModal])

  const handleActionSelect = useCallback(
    (id: string, value: VatDeclarationsDropdownActionType) => {
      switch (value) {
        case VatDeclarationsDropdownActionType.ChangePeriod: {
          openChangePeriodModal()
          break
        }
        case VatDeclarationsDropdownActionType.Export: {
          openExportModal()
          break
        }
        case VatDeclarationsDropdownActionType.SubmitManually: {
          openSettlementModal()
          break
        }
        case VatDeclarationsDropdownActionType.VoidSettlement: {
          openVoidSettlementModal()
          break
        }
        case VatDeclarationsDropdownActionType.VoidPayment: {
          openVoidPaymentModal()
          break
        }
      }
    },
    [openChangePeriodModal, openExportModal, openVoidSettlementModal, openVoidPaymentModal, openSettlementModal],
  )

  const renderSideActionsPrimaryButton = useCallback(() => {
    if (!shouldShowPrimaryButton) {
      return null
    }

    if (organization?.countryId === 'NL') {
      return (
        <SideActionsPrimaryButtonNL onShowIntegrationForm={onShowIntegrationForm} buttonVariant={submitButtonVariant} />
      )
    }

    if (organization?.countryId === 'DK') {
      return (
        <SideActionsPrimaryButtonDK onShowIntegrationForm={onShowIntegrationForm} buttonVariant={submitButtonVariant} />
      )
    }

    return <SideActionsPrimaryButton />
  }, [onShowIntegrationForm, organization?.countryId, shouldShowPrimaryButton, submitButtonVariant])

  if (!vatDeclaration) {
    return null
  }

  return (
    <>
      <ButtonsGroup>
        {shouldShowDefinePayoutButton && (
          <Button onClick={handleDefinePayoutButtonClick} variant="outlined">
            {t('vat_declarations.sidebar.define_payout')}
          </Button>
        )}
        {renderSideActionsPrimaryButton()}
        {shouldShowMoreDropdown && (
          <ButtonDropdown
            items={vatDeclarationsMenuItems}
            onSelect={handleActionSelect}
            placement="bottom-end"
            triggerElement={<IconButton icon="ellipsis" size="md" variant="outlined" />}
          />
        )}
      </ButtonsGroup>
      <ActionModals />
    </>
  )
}
